/**
 * useDashboardPermissions Hook
 * 
 * Single Source of Truth per i permessi dashboard.
 * Legge SOLO da user.permissions.hubs[].apps.dashboard (Firestore hub_permissions)
 * 
 * Elimina la necessità di logica duplicata in ogni componente.
 */

import { useMemo } from 'react';
import { useAuth } from '@/app/providers/AuthProvider';
import { logger } from '@/lib/logger';
import { SCOPES } from '@/utils/scopeMapping';

export interface DashboardPermissions {
  /** Può accedere alla dashboard (tab Panoramica) */
  canAccess: boolean;
  
  /** Ha accesso completo a tutti gli ambiti */
  fullAccess: boolean;
  
  /** Può filtrare per CDC */
  canFilterCdc: boolean;
  
  /** Lista ambiti disponibili (tutti se fullAccess, altrimenti quelli assegnati) */
  scopes: string[];
  
  /** È amministratore della piattaforma */
  isAdmin: boolean;

  /** Può accedere alla tab "Dettagli" */
  canAccessDettagli: boolean;

  /** Può accedere alla tab "Analisi" */
  canAccessAnalisi: boolean;
}

type RawDashboardPermissions = {
  canAccess?: boolean;
  full_access?: boolean;
  can_filter_cdc?: boolean;
  scopes?: string[];
  can_access_dettagli?: boolean;
  can_access_analisi?: boolean;
};

/**
 * Hook per ottenere i permessi dashboard dell'utente corrente
 * 
 * @returns Oggetto con tutti i permessi dashboard
 * 
 * @example
 * const { fullAccess, scopes, canFilterCdc } = useDashboardPermissions();
 * 
 * if (fullAccess) {
 *   // Mostra tutti i dati
 * } else {
 *   // Filtra per scopes assegnati
 * }
 */
export function useDashboardPermissions(): DashboardPermissions {
  const { user, isSuperAdmin, adminScope } = useAuth();
  
  return useMemo(() => {
    // Default: nessun permesso
    if (!user) {
      return {
        canAccess: false,
        fullAccess: false,
        canFilterCdc: false,
        scopes: [],
        isAdmin: false,
        canAccessDettagli: false,
        canAccessAnalisi: false
      };
    }
    
    // Solo Super Admin e Admin di Area del business hanno accesso completo.
    // Un Platform Admin non eredita automaticamente i permessi dell'hub business.
    if (isSuperAdmin || adminScope.includes('business')) {
      return {
        canAccess: true,
        fullAccess: true,
        canFilterCdc: true,
        scopes: [...SCOPES],
        isAdmin: user.isAdmin,
        canAccessDettagli: true,
        canAccessAnalisi: true
      };
    }
    
    // Leggi permessi dashboard da Firestore hub_permissions
    // SINGLE SOURCE OF TRUTH
    const dashboardPerms = user.permissions?.hubs
      ?.find(h => h.hubId === 'business')
      ?.apps?.dashboard as unknown as RawDashboardPermissions | undefined;
    logger.debug('🧪 DashboardPermissions Debug - raw dashboard perms from user.permissions', {
      email: user.email,
      dashboardPerms,
      businessHub: user.permissions?.hubs?.find(h => h.hubId === 'business') || null
    });
    
    // Se non ha permessi dashboard, nega accesso
    if (!dashboardPerms) {
      return {
        canAccess: false,
        fullAccess: false,
        canFilterCdc: false,
        scopes: [],
        isAdmin: false,
        canAccessDettagli: false,
        canAccessAnalisi: false
      };
    }
    
    // Determina full_access
    const fullAccess = dashboardPerms.full_access === true;
    
    // Determina scopes disponibili
    let scopes: string[] = [];
    if (fullAccess) {
      // Full access → tutti gli ambiti
      scopes = [...SCOPES];
    } else if (Array.isArray(dashboardPerms.scopes)) {
      // Scopes limitati → filtra '*' (non è un ambito valido)
      scopes = dashboardPerms.scopes.filter((s: string) => s !== '*');
    }
    
    const effectivePermissions = {
      canAccess: dashboardPerms.canAccess === true,
      // NOTA: `fullAccess` qui corrisponde al perimetro DATI completo
      // (dataFullAccess lato server), non necessariamente al flag "Gestione
      // e Controllo": può derivare anche dal vecchio permesso
      // "Leggi dati sensibili business", che non abilita da solo le tab.
      fullAccess,
      canFilterCdc: dashboardPerms.can_filter_cdc === true,
      scopes,
      isAdmin: false,
      // Fail-closed: senza permesso esplicito (o "Gestione e Controllo") le
      // tab "Dettagli"/"Analisi" restano nascoste. Già calcolato correttamente
      // in authService.buildLegacyPermissions.
      canAccessDettagli: dashboardPerms.can_access_dettagli === true,
      canAccessAnalisi: dashboardPerms.can_access_analisi === true
    };
    logger.debug('🧪 DashboardPermissions Debug - effective dashboard permissions', {
      email: user.email,
      effectivePermissions
    });

    return effectivePermissions;
  }, [adminScope, isSuperAdmin, user]);
}
